import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsRight } from 'lucide-react';
import type { CaptchaTracePoint } from '../../types';
import { CaptchaViewProps, pngSrc, toImagePoint } from './captchaUtils';
import { CaptchaHelp } from './CaptchaHelp';

/** Drag the handle until the piece fits its gap. Submits on release. */
export function SliderPuzzleCaptcha({ challenge, startedAt, disabled, onSubmit }: CaptchaViewProps) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const trace = useRef<CaptchaTracePoint[]>([]);
  const dragOffset = useRef(0);
  const [pieceWidth, setPieceWidth] = useState(0);
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const maxX = Math.max(0, challenge.width - pieceWidth);

  const positionFor = (event: React.PointerEvent) => {
    const point = toImagePoint(event, trackRef.current!, challenge.width, startedAt);
    return { point, x: Math.min(maxX, Math.max(0, point.x - dragOffset.current)) };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const { point } = positionFor(event);
    dragOffset.current = point.x - x;
    trace.current = [{ ...point, x }];
    setDragging(true);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const { point, x: next } = positionFor(event);
    trace.current.push({ ...point, x: next });
    setX(next);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    const { point, x: final } = positionFor(event);
    trace.current.push({ ...point, x: final });
    onSubmit({ x: final, trace: trace.current });
  };

  const percent = (value: number) => `${(value / challenge.width) * 100}%`;

  return (
    <div className="captcha-slider">
      <CaptchaHelp type="slider" />
      <div className="captcha-image-frame" style={{ aspectRatio: `${challenge.width} / ${challenge.height}` }}>
        <img className="captcha-image" src={pngSrc(challenge.image)} alt="" draggable={false} />
        {challenge.pieceImage && (
          <img
            className="captcha-piece"
            src={pngSrc(challenge.pieceImage)}
            alt=""
            draggable={false}
            onLoad={e => setPieceWidth(e.currentTarget.naturalWidth)}
            style={{
              left: percent(x),
              top: `${((challenge.pieceY ?? 0) / challenge.height) * 100}%`,
              width: percent(pieceWidth),
            }}
          />
        )}
      </div>
      <div
        ref={trackRef}
        className={`captcha-track ${dragging ? 'captcha-track-active' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setDragging(false)}
        role="slider"
        aria-label={t('captcha.slider.instruction')}
        aria-valuemin={0}
        aria-valuemax={Math.round(maxX)}
        aria-valuenow={Math.round(x)}
      >
        <div className="captcha-track-fill" style={{ width: percent(x + pieceWidth / 2) }} />
        <div className="captcha-handle" style={{ left: percent(x), width: percent(pieceWidth || 46) }}>
          <ChevronsRight size={18} />
        </div>
      </div>
    </div>
  );
}
